import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import EligibleStudentPicker from './EligibleStudentPicker';

const STATUS_LABELS = {
  planned:   { label: 'Запланировано', cls: 'pay-st pay-st--draft' },
  conducted: { label: 'Проведено',     cls: 'pay-st pay-st--ok' },
  canceled:  { label: 'Отменено',      cls: 'pay-st pay-st--mute' },
};

function shortTime(t) { return (t || '').slice(0, 5); }
function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtDate(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}

export default function ExtraScheduleEntryModal({ entryId, onClose }) {
  const can = useCan();
  const canWrite  = can('extra.schedule', 'write');
  const canDelete = can('extra.schedule', 'delete');
  // Правка суммы «к списанию» — денежное право реестра начислений,
  // учителям недоступно.
  const canEditPrice = can('extra.charges', 'write');

  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  // Inline-формы — без prompt/confirm
  const [addOpen, setAddOpen]       = useState(false);
  const [conductOpen, setConductOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [destroyOpen, setDestroyOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [priceEdit, setPriceEdit]     = useState(null); // {studentId, name, value}
  const [warning, setWarning]         = useState(null); // подсказка после правки суммы

  // Редактирование шапки (date / timeId / roomId / comment) для planned-занятий
  const [editOpen, setEditOpen] = useState(false);
  const [editDate, setEditDate]       = useState('');
  const [editTimeId, setEditTimeId]   = useState('');
  const [editRoomId, setEditRoomId]   = useState('');
  const [editComment, setEditComment] = useState('');
  const [times, setTimes]             = useState([]);
  const [rooms, setRooms]             = useState([]);

  useEffect(() => {
    if (!editOpen) return;
    api.get('/api/extra-lesson-times').then((r) => setTimes((r.rows || []).filter((x) => !x.archive))).catch(() => {});
    api.get('/api/rooms').then((r) => setRooms(r.rows || [])).catch(() => {});
  }, [editOpen]);

  // Несохранённые данные: открыта форма правки шапки или введена причина
  // отмены — тогда случайный клик мимо окна / Escape сначала спросит.
  const hasUnsavedChanges = () =>
    editOpen || priceEdit !== null || (cancelOpen && cancelReason.trim() !== '');
  const attemptClose = () => {
    if (busy) return;
    if (hasUnsavedChanges() && !window.confirm('Закрыть окно без сохранения данных?')) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, busy, editOpen, cancelOpen, cancelReason]);

  const reload = () => {
    setError(null);
    api.get('/api/extra-schedule/' + entryId)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'));
  };
  useEffect(reload, [entryId]);

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); reload(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Не удалось'); }
    finally { setBusy(false); }
  };

  const onAddStudent = (s) => {
    act(() => api.post(`/api/extra-schedule/${entryId}/students`, { studentId: s.id }));
    setAddOpen(false);
  };
  const onRemoveStudent = (sid) => {
    act(() => api.delete(`/api/extra-schedule/${entryId}/students/${sid}`));
  };
  const onSaveEdit = () => {
    const body = {};
    if (editDate)              body.date    = editDate;
    if (editTimeId)             body.timeId  = parseInt(editTimeId, 10);
    if (editRoomId !== '')      body.roomId  = editRoomId === '__none__' ? null : parseInt(editRoomId, 10);
    body.comment = editComment;
    act(() => api.put('/api/extra-schedule/' + entryId, body));
    setEditOpen(false);
  };
  const beginEdit = () => {
    if (!data?.entry) return;
    setEditDate(data.entry.date || '');
    setEditTimeId(data.entry.timeId ? String(data.entry.timeId) : '');
    setEditRoomId(data.entry.roomId ? String(data.entry.roomId) : '__none__');
    setEditComment(data.entry.comment || '');
    setEditOpen(true);
  };
  const onConduct = () => {
    act(() => api.post(`/api/extra-schedule/${entryId}/conduct`, {}));
    setConductOpen(false);
  };
  const onCancel = () => {
    act(() => api.post(`/api/extra-schedule/${entryId}/cancel`, { reason: cancelReason || '' }));
    setCancelOpen(false); setCancelReason('');
  };
  const onDestroy = () => {
    act(async () => { await api.delete('/api/extra-schedule/' + entryId); onClose(); });
    setDestroyOpen(false);
  };
  const onRestore = () => {
    act(() => api.post(`/api/extra-schedule/${entryId}/restore`, {}));
    setRestoreOpen(false);
  };
  const onSavePrice = async () => {
    if (!priceEdit) return;
    const value = Number(String(priceEdit.value).replace(',', '.'));
    setBusy(true);
    try {
      const res = await api.put(
        `/api/extra-schedule/${entryId}/students/${priceEdit.studentId}`,
        { priceStudent: value }
      );
      setWarning(res.warning || null);
      setPriceEdit(null);
      reload();
    } catch (e) {
      // Ошибку (например, замок данных) показываем прямо в форме —
      // верхний блок ошибок модалки может быть вне экрана.
      setPriceEdit({
        ...priceEdit,
        error: e instanceof ApiError ? e.message : 'Не удалось сохранить',
      });
    } finally {
      setBusy(false);
    }
  };

  const e        = data?.entry;
  const students = data?.students || [];
  const studentCharges = data?.studentCharges || [];
  const teacherCharges = data?.teacherCharges || [];
  const isPlanned   = e?.status === 'planned';
  const isConducted = e?.status === 'conducted';
  const isCanceled  = e?.status === 'canceled';
  const isIndividual = e?.lesson?.type === 'individual';
  const statusInfo  = e ? (STATUS_LABELS[e.status] || { label: e.status, cls: 'pay-st' }) : null;

  // Сводки по начислениям
  const chargesSummary = (() => {
    const stPaid    = studentCharges.filter((c) => c.onecUploadedAt).reduce((s, c) => s + c.amount, 0);
    const stPending = studentCharges.filter((c) => !c.onecUploadedAt).reduce((s, c) => s + c.amount, 0);
    const tPaid     = teacherCharges.filter((c) => c.onecUploadedAt).reduce((s, c) => s + c.amount, 0);
    const tPending  = teacherCharges.filter((c) => !c.onecUploadedAt).reduce((s, c) => s + c.amount, 0);
    return { stPaid, stPending, tPaid, tPending };
  })();

  return (
    <div className="sd-overlay" onClick={(ev) => { if (ev.target.classList.contains('sd-overlay')) attemptClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            Занятие #{entryId}
            {e && (
              <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--fg-muted)', fontWeight: 400 }}>
                {e.date} · {shortTime(e.slotStart)}–{shortTime(e.slotEnd)}
              </span>
            )}
          </h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="sd-edit__error">{error}</div>}
          {!data && !error && <div className="ref-empty">Загрузка…</div>}

          {data && e && (
            <>
              {/* Сводка */}
              <div className="pay-summary">
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Статус</span>
                  <span className={statusInfo.cls}>{statusInfo.label}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Тип</span>
                  <span>{isIndividual ? 'Индивидуально' : 'Группа'}</span>
                </div>
                <div className="pay-summary__row pay-summary__row--full">
                  <span className="pay-summary__label">Предмет</span>
                  <span>
                    {e.lesson?.subject?.name || '—'}
                    {e.lesson?.subject?.onecCode && (
                      <span className="muted" style={{ marginLeft: 8, fontFamily: 'var(--font-mono)' }}>
                        {e.lesson.subject.onecCode}
                      </span>
                    )}
                  </span>
                </div>
                <div className="pay-summary__row pay-summary__row--full">
                  <span className="pay-summary__label">Учитель</span>
                  <span>{e.lesson?.teacher?.name || '—'}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Кабинет</span>
                  <span>{e.roomName || '—'}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">С ученика</span>
                  <span>{fmtMoney(e.priceStudentSnapshot)}</span>
                </div>
                <div className="pay-summary__row">
                  <span className="pay-summary__label">Учителю</span>
                  <span>
                    {(() => {
                      const unit = Number(e.priceTeacherSnapshot) || 0;
                      const isGroup = e.lesson?.type === 'group';
                      const n = students.length;
                      if (isGroup && n > 0) {
                        return (
                          <>
                            {fmtMoney(unit * n)}{' '}
                            <span className="muted" style={{ fontSize: 11 }}>
                              ({fmtMoney(unit)} × {n} уч.)
                            </span>
                          </>
                        );
                      }
                      return fmtMoney(unit);
                    })()}
                  </span>
                </div>
                {e.conductedAt && (
                  <div className="pay-summary__row">
                    <span className="pay-summary__label">Проведено</span>
                    <span>{fmtDate(e.conductedAt)}</span>
                  </div>
                )}
                {e.canceledAt && (
                  <div className="pay-summary__row pay-summary__row--full">
                    <span className="pay-summary__label">Отменено</span>
                    <span>
                      {fmtDate(e.canceledAt)}
                      {e.cancelReason && <span className="muted"> · {e.cancelReason}</span>}
                    </span>
                  </div>
                )}
                {e.comment && (
                  <div className="pay-summary__row pay-summary__row--full">
                    <span className="pay-summary__label">Комментарий</span>
                    <span>{e.comment}</span>
                  </div>
                )}
              </div>

              {/* Главный toggle: «Занятие проведено». Если планируется в будущем
                  — чекбокс пуст, ничего не списывается. Когда фактически провели —
                  отмечают и запускаются начисления (этап 5). */}
              {canWrite && (
                <label className={'es-conducted-toggle' + (isConducted ? ' is-on' : '')}>
                  <input
                    type="checkbox"
                    checked={isConducted}
                    disabled={busy || isCanceled}
                    onChange={() => {
                      if (!isConducted) setConductOpen(true);
                      else              setCancelOpen(true);
                    }}
                  />
                  <span className="es-conducted-toggle__label">
                    {isConducted
                      ? 'Занятие проведено'
                      : isCanceled
                        ? 'Занятие отменено'
                        : 'Занятие запланировано (не проведено)'}
                  </span>
                  <span className="es-conducted-toggle__hint">
                    {isPlanned
                      ? 'Поставьте галочку, когда занятие реально состоится. Только тогда оно учитывается в начислениях.'
                      : isConducted
                        ? 'Снимите галочку, чтобы отменить занятие (с указанием причины).'
                        : ''}
                  </span>
                </label>
              )}

              {/* Второстепенные действия */}
              <div className="pay-actions">
                {canWrite && isPlanned && !editOpen && (
                  <button className="btn btn--sm" disabled={busy} onClick={beginEdit}>
                    <Icon name="edit" size={12} /> Изменить
                  </button>
                )}
                {canWrite && isCanceled && !restoreOpen && (
                  <button className="btn btn--sm" disabled={busy} onClick={() => setRestoreOpen(true)}>
                    Вернуть занятие
                  </button>
                )}
                {canDelete && !isConducted && !destroyOpen && (
                  <button className="btn btn--sm btn--danger" disabled={busy} onClick={() => setDestroyOpen(true)}>
                    Удалить занятие
                  </button>
                )}
              </div>

              {/* Inline-форма редактирования (только для planned) */}
              {editOpen && (
                <div className="pay-add">
                  <div className="pay-add__grid">
                    <div className="sd-edit__field">
                      <label className="sd-edit__label">Дата *</label>
                      <input className="sd-edit__input" type="date"
                             value={editDate} onChange={(ev) => setEditDate(ev.target.value)} />
                    </div>
                    <div className="sd-edit__field">
                      <label className="sd-edit__label">Слот *</label>
                      <select className="sd-edit__select"
                              value={editTimeId} onChange={(ev) => setEditTimeId(ev.target.value)}>
                        <option value="">— выберите —</option>
                        {times.map((t) => (
                          <option key={t.id} value={t.id}>
                            {(t.timeStart || '').slice(0,5)} — {(t.timeEnd || '').slice(0,5)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sd-edit__field sd-edit__field--full">
                      <label className="sd-edit__label">Кабинет</label>
                      <select className="sd-edit__select"
                              value={editRoomId} onChange={(ev) => setEditRoomId(ev.target.value)}>
                        <option value="__none__">— не указан —</option>
                        {rooms.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                      </select>
                    </div>
                    <div className="sd-edit__field sd-edit__field--full">
                      <label className="sd-edit__label">Комментарий</label>
                      <input className="sd-edit__input"
                             value={editComment} onChange={(ev) => setEditComment(ev.target.value)} />
                    </div>
                  </div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setEditOpen(false)} disabled={busy}>Отмена</button>
                    <button className="btn btn--primary btn--sm" onClick={onSaveEdit} disabled={busy}>
                      {busy ? 'Сохраняем…' : 'Сохранить'}
                    </button>
                  </div>
                </div>
              )}

              {conductOpen && (
                <div className="pay-inline pay-inline--ok">
                  <div className="pay-inline__title">Отметить занятие проведённым?</div>
                  <div className="pay-inline__hint">
                    После этого состав учеников нельзя будет менять. На этапе 5 здесь же запустятся
                    списания с учеников и начисление учителю.
                  </div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setConductOpen(false)} disabled={busy}>Отмена</button>
                    <button className="btn btn--primary btn--sm" onClick={onConduct} disabled={busy}>
                      {busy ? 'Сохраняем…' : 'Подтвердить'}
                    </button>
                  </div>
                </div>
              )}
              {cancelOpen && (
                <div className="pay-inline">
                  <div className="pay-inline__title">Отменить занятие</div>
                  <input
                    className="sd-edit__input"
                    placeholder="Причина (необязательно)"
                    value={cancelReason}
                    onChange={(ev) => setCancelReason(ev.target.value)}
                    autoFocus
                  />
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={busy}>Не отменять</button>
                    <button className="btn btn--sm btn--danger" onClick={onCancel} disabled={busy}>
                      {busy ? 'Отменяем…' : 'Отменить'}
                    </button>
                  </div>
                </div>
              )}
              {restoreOpen && (
                <div className="pay-inline pay-inline--ok">
                  <div className="pay-inline__title">
                    {e.conductedAt
                      ? 'Вернуть занятие как проведённое?'
                      : 'Вернуть занятие в запланированные?'}
                  </div>
                  <div className="pay-inline__hint">
                    {e.conductedAt
                      ? 'Отмена будет снята, занятие снова станет проведённым. Начисления ученикам и учителю восстановятся: невыгруженное сторно удалится, выгруженное в 1С — компенсируется встречным начислением.'
                      : 'Отмена будет снята, занятие снова станет запланированным (без начислений).'}
                  </div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setRestoreOpen(false)} disabled={busy}>Отмена</button>
                    <button className="btn btn--primary btn--sm" onClick={onRestore} disabled={busy}>
                      {busy ? 'Возвращаем…' : 'Вернуть'}
                    </button>
                  </div>
                </div>
              )}
              {destroyOpen && (
                <div className="pay-inline pay-inline--danger">
                  <div className="pay-inline__title">Удалить занятие?</div>
                  <div className="pay-inline__hint">Действие необратимо. Состав учеников и история тоже удалятся.</div>
                  <div className="pay-inline__actions">
                    <button className="btn btn--sm" onClick={() => setDestroyOpen(false)} disabled={busy}>Не удалять</button>
                    <button className="btn btn--sm btn--danger" onClick={onDestroy} disabled={busy}>
                      {busy ? 'Удаляем…' : 'Удалить'}
                    </button>
                  </div>
                </div>
              )}

              {/* Состав учеников */}
              <div className="pay-section">
                <div className="pay-section__head">
                  <h4>Ученики ({students.length})</h4>
                  {canWrite && isPlanned && !addOpen
                    && !(isIndividual && students.length >= 1)
                    && (
                      <button className="btn btn--sm" onClick={() => setAddOpen(true)} disabled={busy}>
                        <Icon name="plus" size={12} /> Добавить
                      </button>
                    )}
                </div>

                {addOpen && (
                  <div className="pay-add">
                    <EligibleStudentPicker
                      date={e.date}
                      onPick={onAddStudent}
                    />
                    <div className="pay-inline__actions">
                      <button className="btn btn--sm" onClick={() => setAddOpen(false)} disabled={busy}>Закрыть</button>
                    </div>
                  </div>
                )}

                <div className="table-wrap">
                  <table className="table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Ученик</th>
                        <th style={{ width: 130 }}>Договор</th>
                        <th style={{ width: 110, textAlign: 'right' }}>К списанию</th>
                        {isPlanned && canWrite && <th style={{ width: 36 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.id}>
                          <td>{s.studentName}</td>
                          <td className="muted">№ {s.agreementNumber || '—'}</td>
                          <td className="mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {fmtMoney(s.priceStudentCharged)}
                            {canEditPrice && !isCanceled && (
                              <button
                                className="agr-scan__iconbtn"
                                style={{ marginLeft: 6, verticalAlign: 'middle' }}
                                title="Изменить сумму (перерасчёт по заявлению)"
                                disabled={busy}
                                onClick={() => {
                                  setWarning(null);
                                  setPriceEdit({
                                    studentId: s.studentId,
                                    name:      s.studentName,
                                    value:     s.priceStudentCharged,
                                  });
                                }}
                              >
                                <Icon name="edit" size={11} />
                              </button>
                            )}
                          </td>
                          {isPlanned && canWrite && (
                            <td>
                              <button
                                className="agr-scan__iconbtn agr-scan__iconbtn--danger"
                                onClick={() => onRemoveStudent(s.studentId)}
                                title="Убрать"
                                disabled={busy}
                              >
                                <Icon name="x" size={11} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {students.length === 0 && (
                        <tr>
                          <td colSpan={isPlanned && canWrite ? 4 : 3}
                              style={{ padding: 12, textAlign: 'center', color: 'var(--fg-muted)' }}>
                            Учеников пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {priceEdit && (
                  <div className="pay-inline">
                    <div className="pay-inline__title">Изменить сумму: {priceEdit.name}</div>
                    <input
                      className="sd-edit__input"
                      type="number" min="0" step="0.01"
                      style={{ maxWidth: 180 }}
                      value={priceEdit.value}
                      onChange={(ev) => setPriceEdit({ ...priceEdit, value: ev.target.value })}
                      autoFocus
                    />
                    <div className="pay-inline__hint">
                      Перерасчёт по заявлению (например, месячная продлёнка по дням
                      посещения). Изменится «к списанию» на этом занятии и начисление
                      ученика. Если месяц уже выгружен в 1С — появится подсказка о
                      перевыгрузке. Начисление учителю не меняется.
                    </div>
                    {priceEdit.error && (
                      <div className="sd-edit__error" style={{ marginTop: 6 }}>{priceEdit.error}</div>
                    )}
                    <div className="pay-inline__actions">
                      <button className="btn btn--sm" onClick={() => setPriceEdit(null)} disabled={busy}>Отмена</button>
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={onSavePrice}
                        disabled={busy || String(priceEdit.value).trim() === ''
                          || Number.isNaN(Number(String(priceEdit.value).replace(',', '.')))}
                      >
                        {busy ? 'Сохраняем…' : 'Сохранить'}
                      </button>
                    </div>
                  </div>
                )}
                {warning && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 6, fontSize: 12.5,
                    background: 'var(--panel-2)', border: '1px solid var(--border-strong)',
                  }}>
                    ⚠ {warning}
                  </div>
                )}
              </div>

              {/* Начисления (только когда занятие проведено или отменено после проведения) */}
              {(isConducted || isCanceled) && (studentCharges.length > 0 || teacherCharges.length > 0) && (
                <div className="pay-section">
                  <div className="pay-section__head">
                    <h4>Начисления</h4>
                    <span className="muted" style={{ fontSize: 11 }}>
                      ученикам {fmtMoney(chargesSummary.stPaid + chargesSummary.stPending)}
                      {' · '}учителю {fmtMoney(chargesSummary.tPaid + chargesSummary.tPending)}
                    </span>
                  </div>

                  <div className="table-wrap">
                    <table className="table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Кому</th>
                          <th>Получатель</th>
                          <th style={{ width: 110, textAlign: 'right' }}>Сумма</th>
                          <th style={{ width: 130 }}>1С</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentCharges.map((c) => {
                          const ss = students.find((x) => x.studentId === c.studentId);
                          return (
                            <tr key={'s' + c.id}>
                              <td className="muted">Ученик</td>
                              <td>
                                {ss?.studentName || ('#' + c.studentId)}
                                {c.kind === 'extra_lesson_storno' && (
                                  <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>· сторно</span>
                                )}
                              </td>
                              <td className="mono" style={{ textAlign: 'right',
                                color: c.amount < 0 ? 'var(--red-fg)' : undefined }}>
                                {fmtMoney(c.amount)}
                              </td>
                              <td>
                                {c.onecUploadedAt
                                  ? <span className="pay-st pay-st--ok">выгружено</span>
                                  : <span className="pay-st pay-st--pending">ожидает</span>}
                              </td>
                            </tr>
                          );
                        })}
                        {teacherCharges.map((c) => (
                          <tr key={'t' + c.id}>
                            <td className="muted">Учитель</td>
                            <td>
                              {e?.lesson?.teacher?.name || ('#' + c.teacherId)}
                              {c.kind === 'extra_lesson_storno' && (
                                <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>· сторно</span>
                              )}
                            </td>
                            <td className="mono" style={{ textAlign: 'right',
                              color: c.amount < 0 ? 'var(--red-fg)' : undefined }}>
                              {fmtMoney(c.amount)}
                            </td>
                            <td>
                              {c.onecUploadedAt
                                ? <span className="pay-st pay-st--ok">выгружено</span>
                                : <span className="pay-st pay-st--pending">ожидает</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
